<script setup lang="ts">
import type { Category } from '../../types'
import SkeletonLoader from '../shared/SkeletonLoader.vue'
import EditableLabel from '../shared/EditableLabel.vue'

const props = defineProps<{
  categories: Category[]
  isLoading: boolean
}>()

const emit = defineEmits<{
  selectCategory: [catId: number, hasChildren: boolean]
}>()

function select(cat: Category) {
  emit('selectCategory', cat.categoryId, cat.childCount > 0)
}
</script>

<template>
  <div class="category-grid">
    <SkeletonLoader v-if="isLoading" type="text" :lines="8" />

    <div v-else-if="categories.length === 0" class="category-grid__empty">
      Ingen kategorier funnet
    </div>

    <ul v-else class="category-grid__list">
      <li v-for="cat in categories" :key="cat.categoryId" class="category-grid__item">
        <button class="category-grid__btn" @click="select(cat)">
          <EditableLabel
            class="category-grid__name"
            :label-key="`cat:${cat.categoryId}`"
            :fallback="cat.categoryName"
          />
          <span class="category-grid__meta">
            <span v-if="cat.articleCount" class="category-grid__count">{{ cat.articleCount }}</span>
            <span v-if="cat.childCount > 0" class="category-grid__arrow">&#9656;</span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.category-grid {
  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__btn {
    width: 100%;
    text-align: left;
    background: white;
    border: 1px solid #e5e7eb;
    padding: 10px 14px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px;
    font-size: 0.9rem;
    transition: background 0.1s;

    &:hover { background: #f0f4f8; }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__count {
    background: #e5e7eb;
    color: #555;
    font-size: 0.75rem;
    padding: 1px 8px;
    border-radius: 10px;
  }

  &__arrow {
    color: #999;
  }

  &__empty {
    text-align: center;
    padding: 2rem;
    color: #888;
  }
}
</style>
